import { useCallback, useEffect, useState } from 'react';
import { AuthUser, getCurrentUser, signIn as svcSignIn, signOut as svcSignOut, isAuthConfigured } from '../services/auth';

export interface UseAuthResult {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => void;
  authConfigured: boolean;
}

export function useAuth(): UseAuthResult {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const authConfigured = isAuthConfigured();

  useEffect(() => {
    if (!authConfigured) { setLoading(false); return; }
    getCurrentUser().then((u) => setUser(u)).catch(() => setUser(null)).finally(() => setLoading(false));
  }, [authConfigured]);

  const signIn = useCallback(async (username: string, password: string) => {
    setError(null); setLoading(true);
    try {
      const u = await svcSignIn(username, password);
      setUser(u);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed');
      throw e;
    } finally { setLoading(false); }
  }, []);

  const signOut = useCallback(() => {
    svcSignOut();
    setUser(null);
  }, []);

  return { user, loading, error, signIn, signOut, authConfigured };
}
